import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { buildTuningRunDetailPath } from '../services/accuracyTuningPaths';
import type { MetricCountDrilldown, MetricCountKind } from '../services/accuracyCountDrilldown';
import type { EvaluationHistoryRow } from '../services/accuracyEvaluationHistory';
import type { AccuracyErrorType, AccuracyReportResult, CategoryMetricRow } from '../types/accuracy';
import { formatAccuracyStatusLabel, isAccuracyStatusInProgress } from '../services/accuracyStatusLabels';
import '../pages/AccuracyReportPage.css';
import '../pages/AccuracyTuningPages.css';

export const METRIC_BALANCE_LABEL = '総合スコア';
const METRIC_BALANCE_HINT = '適合率と再現率のバランスを示す指標です';

const COUNT_CORRECT_LABEL = '正検知';
const COUNT_FALSE_ALARM_LABEL = '誤検知';
const COUNT_MISSED_LABEL = '検知漏れ';
const COUNT_WRONG_BOX_LABEL = '枠ずれ';

const COUNT_CORRECT_HINT = 'システムの判定が最終結果と一致した件数';
const COUNT_FALSE_ALARM_HINT = 'システムが検出したが、レビューで取り消された件数（誤検知）';
const COUNT_MISSED_HINT = 'システムが見逃したが、レビューで確認された件数（検知漏れ）';
const COUNT_WRONG_BOX_HINT = '違反はあるが枠の位置が修正された件数';

const EXISTENCE_METRIC_COLS = 6;
const VIOLATION_METRIC_COLS = 7;

type AccuracyTableRow = CategoryMetricRow & { isOverall?: boolean; note?: string };

function fmtPct(x: number | null | undefined): string {
  if (x == null) return '—';
  return `${(x * 100).toFixed(1)}%`;
}

function formatIncompleteEvaluationLabel(status: string): string {
  if (isAccuracyStatusInProgress(status)) {
    return '計算中';
  }
  if (status === 'failed') {
    return '計算に失敗しました';
  }
  return `結果がありません（${formatAccuracyStatusLabel(status)}）`;
}

function MetricCountCell({ count, onOpen }: { count: number; onOpen: () => void }) {
  if (count === 0) {
    return <td className="accuracy-metric-count accuracy-metric-count-zero">0</td>;
  }
  return (
    <td className="accuracy-metric-count">
      <button type="button" className="accuracy-count-button" onClick={onOpen} title="URLごとの内訳を表示">
        {String(count)}
      </button>
    </td>
  );
}

function MetricsDataCells({
  row,
  openDrilldown,
}: {
  row: AccuracyTableRow;
  openDrilldown: (
    row: AccuracyTableRow,
    metricType: AccuracyErrorType,
    countKind: MetricCountKind,
    count: number
  ) => void;
}) {
  return (
    <>
      <td>{fmtPct(row.existence.precision)}</td>
      <td>{fmtPct(row.existence.recall)}</td>
      <td>{fmtPct(row.existence.f1)}</td>
      <MetricCountCell
        count={row.existence.tp}
        onOpen={() => openDrilldown(row, 'existence', 'tp', row.existence.tp)}
      />
      <MetricCountCell
        count={row.existence.fp}
        onOpen={() => openDrilldown(row, 'existence', 'fp', row.existence.fp)}
      />
      <MetricCountCell
        count={row.existence.fn}
        onOpen={() => openDrilldown(row, 'existence', 'fn', row.existence.fn)}
      />
      <td>{fmtPct(row.violation.precision)}</td>
      <td>{fmtPct(row.violation.recall)}</td>
      <td>{fmtPct(row.violation.f1)}</td>
      <MetricCountCell
        count={row.violation.tp}
        onOpen={() => openDrilldown(row, 'violation', 'tp', row.violation.tp)}
      />
      <MetricCountCell
        count={row.violation.fp}
        onOpen={() => openDrilldown(row, 'violation', 'fp', row.violation.fp)}
      />
      <MetricCountCell
        count={row.violation.fn}
        onOpen={() => openDrilldown(row, 'violation', 'fn', row.violation.fn)}
      />
      <MetricCountCell
        count={row.violation.wrong_bounding_box ?? 0}
        onOpen={() =>
          openDrilldown(row, 'violation', 'wb', row.violation.wrong_bounding_box ?? 0)
        }
      />
    </>
  );
}

function MetricsTableHead() {
  return (
    <thead>
      <tr>
        <th rowSpan={2} className="accuracy-execution-col-header">
          評価
        </th>
        <th rowSpan={2} className="accuracy-category-col-name">
          カテゴリ
        </th>
        <th colSpan={EXISTENCE_METRIC_COLS} className="accuracy-category-col-group">
          存在フラグ
        </th>
        <th colSpan={VIOLATION_METRIC_COLS} className="accuracy-category-col-group">
          違反検出
        </th>
      </tr>
      <tr>
        <th>適合率</th>
        <th>再現率</th>
        <th title={METRIC_BALANCE_HINT}>{METRIC_BALANCE_LABEL}</th>
        <th title={COUNT_CORRECT_HINT}>{COUNT_CORRECT_LABEL}</th>
        <th title={COUNT_FALSE_ALARM_HINT}>{COUNT_FALSE_ALARM_LABEL}</th>
        <th title={COUNT_MISSED_HINT}>{COUNT_MISSED_LABEL}</th>
        <th>適合率</th>
        <th>再現率</th>
        <th title={METRIC_BALANCE_HINT}>{METRIC_BALANCE_LABEL}</th>
        <th title={COUNT_CORRECT_HINT}>{COUNT_CORRECT_LABEL}</th>
        <th title={COUNT_FALSE_ALARM_HINT}>{COUNT_FALSE_ALARM_LABEL}</th>
        <th title={COUNT_MISSED_HINT}>{COUNT_MISSED_LABEL}</th>
        <th title={COUNT_WRONG_BOX_HINT}>{COUNT_WRONG_BOX_LABEL}</th>
      </tr>
    </thead>
  );
}

function ExecutionLabelCell({
  historyRow,
}: {
  historyRow: EvaluationHistoryRow;
  evalIndex?: number;
}) {
  const tuningDetailHref =
    historyRow.kind === 'tuning' && historyRow.tuning_session_id
      ? historyRow.evaluation_run_id
        ? buildTuningRunDetailPath(
            historyRow.tuning_session_id,
            historyRow.evaluation_run_id
          )
        : `/accuracy/tuning/${encodeURIComponent(historyRow.tuning_session_id)}`
      : null;

  const tuningLinkTitle = historyRow.evaluation_run_id
    ? 'ガイドライン詳細・評価結果を見る'
    : 'ドラフトガイドラインを見る';

  const kindLabel = historyRow.kind === 'baseline' ? 'ベースライン評価' : 'チューニング';
  const primaryTitle = historyRow.label;
  const contextLine =
    historyRow.kind === 'tuning' && historyRow.context ? historyRow.context : undefined;

  return (
    <td className="accuracy-execution-col" rowSpan={1}>
      <div className="accuracy-execution-cell">
        <span className="accuracy-execution-kind">{kindLabel}</span>
        <span className="accuracy-execution-title">
          {tuningDetailHref ? (
            <Link
              to={tuningDetailHref}
              className="accuracy-eval-title-link"
              title={tuningLinkTitle}
            >
              {primaryTitle}
            </Link>
          ) : (
            primaryTitle
          )}
        </span>
        {contextLine ? (
          <span className="accuracy-execution-context">{contextLine}</span>
        ) : null}
        <span className="accuracy-execution-meta">
          <span className={`accuracy-status ${historyRow.status}`}>
            {formatAccuracyStatusLabel(historyRow.status)}
          </span>
          <span>{new Date(historyRow.created_at).toLocaleString('ja-JP')}</span>
        </span>
      </div>
    </td>
  );
}

/** Single evaluation (e.g. tuning run detail page). */
export function AccuracyMetricsTable({
  result,
  onCountClick,
  executionLabel,
}: {
  result: AccuracyReportResult;
  onCountClick: (drilldown: MetricCountDrilldown) => void;
  /** When set, shows a one-row execution column (same layout as merged table). */
  executionLabel?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  const openDrilldown = (
    row: AccuracyTableRow,
    metricType: AccuracyErrorType,
    countKind: MetricCountKind,
    count: number
  ) => {
    if (count === 0) return;
    onCountClick({
      categoryId: row.isOverall ? null : row.category_id,
      categoryName: row.category_name,
      metricType,
      countKind,
    });
  };

  const overall: AccuracyTableRow = {
    category_id: '__overall__',
    category_name: '全体',
    existence: result.existence,
    violation: result.violation,
    isOverall: true,
    note: `対象URL ${result.url_count}件 · ${COUNT_WRONG_BOX_LABEL} ${result.violation.wrong_bounding_box ?? 0}件`,
  };

  const categoryRows: AccuracyTableRow[] = [...result.by_category]
    .sort((a, b) => a.category_name.localeCompare(b.category_name, 'ja'))
    .map((cat) => ({ ...cat, isOverall: false }));

  const hasCategories = categoryRows.length > 0;
  const showExecutionCol = Boolean(executionLabel);

  return (
    <div className="accuracy-category-table-wrap">
      <table className="accuracy-table accuracy-category-table">
        <thead>
          <tr>
            {showExecutionCol ? (
              <th rowSpan={2} className="accuracy-execution-col-header">
                評価
              </th>
            ) : null}
            <th rowSpan={2} className="accuracy-category-col-name">
              カテゴリ
            </th>
            <th colSpan={EXISTENCE_METRIC_COLS} className="accuracy-category-col-group">
              存在フラグ
            </th>
            <th colSpan={VIOLATION_METRIC_COLS} className="accuracy-category-col-group">
              違反検出
            </th>
          </tr>
          <tr>
            <th>適合率</th>
            <th>再現率</th>
            <th title={METRIC_BALANCE_HINT}>{METRIC_BALANCE_LABEL}</th>
            <th title={COUNT_CORRECT_HINT}>{COUNT_CORRECT_LABEL}</th>
            <th title={COUNT_FALSE_ALARM_HINT}>{COUNT_FALSE_ALARM_LABEL}</th>
            <th title={COUNT_MISSED_HINT}>{COUNT_MISSED_LABEL}</th>
            <th>適合率</th>
            <th>再現率</th>
            <th title={METRIC_BALANCE_HINT}>{METRIC_BALANCE_LABEL}</th>
            <th title={COUNT_CORRECT_HINT}>{COUNT_CORRECT_LABEL}</th>
            <th title={COUNT_FALSE_ALARM_HINT}>{COUNT_FALSE_ALARM_LABEL}</th>
            <th title={COUNT_MISSED_HINT}>{COUNT_MISSED_LABEL}</th>
            <th title={COUNT_WRONG_BOX_HINT}>{COUNT_WRONG_BOX_LABEL}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="accuracy-category-row-overall">
            {showExecutionCol ? (
              <td className="accuracy-execution-col">
                <span className="accuracy-execution-title">{executionLabel}</span>
              </td>
            ) : null}
            <td className="accuracy-category-name">
              <div className="accuracy-category-name-cell">
                {hasCategories ? (
                  <button
                    type="button"
                    className="accuracy-category-expand"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                    aria-label={expanded ? 'カテゴリ別を閉じる' : 'カテゴリ別を表示'}
                  >
                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                ) : (
                  <span className="accuracy-category-expand-spacer" aria-hidden />
                )}
                <span>{overall.category_name}</span>
                {overall.note ? (
                  <span className="accuracy-category-note">{overall.note}</span>
                ) : null}
              </div>
            </td>
            <MetricsDataCells row={overall} openDrilldown={openDrilldown} />
          </tr>
          {expanded &&
            categoryRows.map((row) => (
              <tr key={row.category_id} className="accuracy-category-row-detail">
                {showExecutionCol ? <td className="accuracy-execution-col-empty" /> : null}
                <td className="accuracy-category-name accuracy-category-name-indented">
                  {row.category_name}
                </td>
                <MetricsDataCells row={row} openDrilldown={openDrilldown} />
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

function MergedAccuracyMetricsTable({
  historyRows,
  onCountClickForRow,
}: {
  historyRows: EvaluationHistoryRow[];
  onCountClickForRow: (row: EvaluationHistoryRow, drilldown: MetricCountDrilldown) => void;
}) {
  const [expandedEvalIds, setExpandedEvalIds] = useState<Set<string>>(() => new Set());

  const rowsWithResults = useMemo(
    () => historyRows.filter((r) => r.result),
    [historyRows]
  );

  const toggleExpanded = useCallback((evalId: string) => {
    setExpandedEvalIds((prev) => {
      const next = new Set(prev);
      if (next.has(evalId)) next.delete(evalId);
      else next.add(evalId);
      return next;
    });
  }, []);

  const incompleteRows = useMemo(
    () => historyRows.filter((r) => !r.result),
    [historyRows]
  );

  if (rowsWithResults.length === 0 && incompleteRows.length === 0) {
    return null;
  }

  return (
    <div className="accuracy-category-table-wrap accuracy-merged-metrics-wrap">
      <table className="accuracy-table accuracy-category-table accuracy-merged-metrics-table">
        <MetricsTableHead />
        <tbody>
          {rowsWithResults.map((historyRow, evalIndex) => {
            const result = historyRow.result!;
            const expanded = expandedEvalIds.has(historyRow.id);

            const overall: AccuracyTableRow = {
              category_id: '__overall__',
              category_name: '全体',
              existence: result.existence,
              violation: result.violation,
              isOverall: true,
              note: `対象URL ${result.url_count}件 · ${COUNT_WRONG_BOX_LABEL} ${result.violation.wrong_bounding_box ?? 0}件`,
            };

            const categoryRows: AccuracyTableRow[] = [...result.by_category]
              .sort((a, b) => a.category_name.localeCompare(b.category_name, 'ja'))
              .map((cat) => ({ ...cat, isOverall: false }));

            const hasCategories = categoryRows.length > 0;

            const openDrilldown = (
              metricRow: AccuracyTableRow,
              metricType: AccuracyErrorType,
              countKind: MetricCountKind,
              count: number
            ) => {
              if (count === 0) return;
              onCountClickForRow(historyRow, {
                categoryId: metricRow.isOverall ? null : metricRow.category_id,
                categoryName: metricRow.category_name,
                metricType,
                countKind,
              });
            };

            return (
              <React.Fragment key={historyRow.id}>
                <tr
                  className="accuracy-category-row-overall accuracy-merged-eval-row"
                  id={`accuracy-eval-${historyRow.id}`}
                >
                  <ExecutionLabelCell historyRow={historyRow} evalIndex={evalIndex} />
                  <td className="accuracy-category-name">
                    <div className="accuracy-category-name-cell">
                      {hasCategories ? (
                        <button
                          type="button"
                          className="accuracy-category-expand"
                          onClick={() => toggleExpanded(historyRow.id)}
                          aria-expanded={expanded}
                          aria-label={expanded ? 'カテゴリ別を閉じる' : 'カテゴリ別を表示'}
                        >
                          {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </button>
                      ) : (
                        <span className="accuracy-category-expand-spacer" aria-hidden />
                      )}
                      <span>{overall.category_name}</span>
                      {overall.note ? (
                        <span className="accuracy-category-note">{overall.note}</span>
                      ) : null}
                    </div>
                  </td>
                  <MetricsDataCells row={overall} openDrilldown={openDrilldown} />
                </tr>
                {expanded &&
                  categoryRows.map((metricRow) => (
                    <tr
                      key={`${historyRow.id}:${metricRow.category_id}`}
                      className="accuracy-category-row-detail accuracy-merged-eval-detail"
                    >
                      <td className="accuracy-execution-col-empty" aria-hidden />
                      <td className="accuracy-category-name accuracy-category-name-indented">
                        {metricRow.category_name}
                      </td>
                      <MetricsDataCells row={metricRow} openDrilldown={openDrilldown} />
                    </tr>
                  ))}
              </React.Fragment>
            );
          })}
          {incompleteRows.map((historyRow) => (
            <tr key={historyRow.id} className="accuracy-merged-eval-incomplete">
              <ExecutionLabelCell
                historyRow={historyRow}
                evalIndex={historyRows.findIndex((r) => r.id === historyRow.id)}
              />
              <td colSpan={1 + EXISTENCE_METRIC_COLS + VIOLATION_METRIC_COLS} className="accuracy-empty">
                {formatIncompleteEvaluationLabel(historyRow.status)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AccuracyMetricsSection({
  historyRows,
  onCountClickForRow,
  sectionDescription,
  headerAction,
}: {
  historyRows: EvaluationHistoryRow[];
  onCountClickForRow: (row: EvaluationHistoryRow, drilldown: MetricCountDrilldown) => void;
  sectionDescription?: string;
  /** Rendered above the metrics table (e.g. add tuning evaluation). */
  headerAction?: React.ReactNode;
}) {
  return (
    <section className="accuracy-card accuracy-section" aria-labelledby="accuracy-section-title">
      <header className="accuracy-section-header">
        <div className="accuracy-section-header-top">
          <h3 id="accuracy-section-title">精度</h3>
          {headerAction ? <div className="accuracy-section-header-action">{headerAction}</div> : null}
        </div>
        <p className="accuracy-section-desc">
          {sectionDescription ??
            '1 つの表にすべての評価をまとめて表示します。評価列で実行を識別し、行頭の矢印でカテゴリ別の内訳を展開できます。件数をクリックすると URL ごとの内訳を開きます。'}
        </p>
      </header>

      {historyRows.length === 0 ? (
        <p className="accuracy-empty" style={{ padding: '12px 0' }}>
          表示できる評価がありません。
        </p>
      ) : (
        <MergedAccuracyMetricsTable
          historyRows={historyRows}
          onCountClickForRow={onCountClickForRow}
        />
      )}
    </section>
  );
}
